import React from 'react'
import { Card, Badge, Button, Alert } from 'react-bootstrap'
import {
	calcularEstados,
	calcularEstadoPrincipal,
	getConfigEstado
} from 'config/pedidos'
import { CronologiaEstados } from 'components/CronologiaEstados'
import './EstadosTab.css'

function EstadosTab({
	pedido,
	onEstadoChange,
	disabled = false
}) {
	if (!pedido) {
		return (
			<Card className="shadow-sm">
				<Card.Body>
					<div className="estados-sin-pedido">
						<i className="fa fa-exclamation-triangle fa-3x text-muted mb-3"></i>
						<p className="text-muted">No hay información de pedido disponible</p>
					</div>
				</Card.Body>
			</Card>
		)
	}

	const estadosActuales = calcularEstados(pedido)
	const estadoPrincipal = calcularEstadoPrincipal(pedido)

	// ✅ Función para cambios inmediatos de estado (como en Pedidos.js)
	const handleCambiarEstado = async (campo, valor) => {
		try {
			// Llamar directamente al handler que hace la petición API
			await onEstadoChange(campo, valor)
		} catch (error) {
			console.error('Error al cambiar estado:', error)
		}
	}

	// Función para determinar si se puede finalizar un pedido
	const puedeFinalizarPedido = (estados, tipoPedido) => {
		// Debe estar entregado (último estado de la secuencia)
		if (!estados.entregado) return false

		// Debe estar pagado (dependiendo del tipo de pedido)
		// Por ahora asumimos que todos los tipos requieren pago para finalizar
		if (!estados.pagado) return false

		return true
	}

	// Función para obtener el motivo por el cual no se puede finalizar
	const getMotivoNoFinalizacion = (estados, tipoPedido) => {
		const motivos = []

		if (!estados.entregado) {
			motivos.push('el pedido debe estar entregado')
		}

		if (!estados.pagado) {
			motivos.push('el pedido debe estar pagado')
		}

		if (motivos.length === 1) {
			return motivos[0]
		} else if (motivos.length === 2) {
			return motivos.join(' y ')
		}

		return 'no cumple los requisitos necesarios'
	}

	return (
		<Card className="shadow-sm">
			<Card.Header className="bg-light">
				<h6 className="mb-0">
					<i className="fa fa-flag me-2"></i>
					Estados del Pedido
				</h6>
			</Card.Header>
			<Card.Body>
				{/* Estado actual */}
				<div className="estados-actual-section">
					<h6>Estado Actual:</h6>
					<Badge bg={getConfigEstado(estadoPrincipal)?.color || 'secondary'} className="estado-actual-badge">
						<i className={`fa ${getConfigEstado(estadoPrincipal)?.icon || 'fa-question'} me-2`}></i>
						{getConfigEstado(estadoPrincipal)?.label || 'Desconocido'}
					</Badge>
				</div>

				{/* Cronología interactiva de estados */}
				<div className="estados-cronologia-section">
					<h6>Cronología de Estados:</h6>
					<CronologiaEstados
						pedido={pedido}
						soloLectura={disabled}
						onEstadoChange={handleCambiarEstado} // ✅ Usar función de cambio inmediato
					/>
				</div>

				{/* Estados especiales - CON APARIENCIA UNIFORME */}
				<div className="estados-especiales-section">
					<h6>Estados Especiales:</h6>

					{/* Estado de pago - NUEVA APARIENCIA */}
					<div className={`estado-item ${estadosActuales.pagado ? 'estado-completado' : 'estado-pendiente'}`}>
						<div className="estado-badge-container">
							<Badge
								bg={estadosActuales.pagado ? 'success' : 'secondary'}
								text={estadosActuales.pagado ? 'white' : 'dark'}
								className="estado-badge"
							>
								<i className="fa fa-credit-card me-1"></i>
								{estadosActuales.pagado ? 'Pagado' : 'No Pagado'}
							</Badge>
						</div>

						<div className="estado-info">
							{estadosActuales.pagado && (
								<div className="estado-descripcion">
									<small className="text-success">
										<i className="fa fa-check-circle me-1"></i>
										Pago confirmado
									</small>
								</div>
							)}
							{!estadosActuales.pagado && (
								<small className="text-muted">Pendiente de pago</small>
							)}
						</div>

						{!disabled && (
							<div className="estado-acciones">
								<Button
									size="sm"
									variant={estadosActuales.pagado ? 'outline-warning' : 'outline-success'}
									onClick={() => handleCambiarEstado('pagado', !estadosActuales.pagado)}
									className="estado-btn"
								>
									<i className={`fa ${estadosActuales.pagado ? 'fa-times' : 'fa-check'} me-1`}></i>
									{estadosActuales.pagado ? 'Desmarcar' : 'Marcar Pagado'}
								</Button>
							</div>
						)}
					</div>

					{/* Estado finalizado - NUEVA APARIENCIA */}
					<div className={`estado-item ${estadosActuales.finalizado ? 'estado-completado' : 'estado-pendiente'}`}>
						<div className="estado-badge-container">
							<Badge
								bg={estadosActuales.finalizado ? 'dark' : 'secondary'}
								text={estadosActuales.finalizado ? 'white' : 'dark'}
								className="estado-badge"
							>
								<i className="fa fa-flag-checkered me-1"></i>
								{estadosActuales.finalizado ? 'Finalizado' : 'No Finalizado'}
							</Badge>

							{/* Info icon con tooltip explicativo */}
							{!estadosActuales.finalizado && !puedeFinalizarPedido(estadosActuales, pedido.tipo) && (
								<i
									className="fa fa-info-circle text-muted ms-2 estado-info-icon"
									title={getMotivoNoFinalizacion(estadosActuales, pedido.tipo)}
								></i>
							)}
						</div>

						<div className="estado-info">
							{estadosActuales.finalizado && (
								<div className="estado-descripcion">
									<small className="text-success">
										<i className="fa fa-check-circle me-1"></i>
										Pedido cerrado
									</small>
								</div>
							)}
							{!estadosActuales.finalizado && (
								<small className="text-muted">Pedido activo</small>
							)}
						</div>

						{!disabled && (
							<div className="estado-acciones">
								<Button
									size="sm"
									variant={estadosActuales.finalizado ? 'outline-warning' : 'outline-success'}
									onClick={() => handleCambiarEstado('finalizado', !estadosActuales.finalizado)}
									disabled={!estadosActuales.finalizado && !puedeFinalizarPedido(estadosActuales, pedido.tipo)}
									className="estado-btn"
								>
									<i className={`fa ${estadosActuales.finalizado ? 'fa-undo' : 'fa-flag-checkered'} me-1`}></i>
									{estadosActuales.finalizado ? 'Reabrir' : 'Finalizar'}
								</Button>
							</div>
						)}
					</div>

					{/* Mensaje explicativo cuando no se puede finalizar */}
					{!estadosActuales.finalizado && !puedeFinalizarPedido(estadosActuales, pedido.tipo) && (
						<Alert variant="info" className="estados-info-finalizacion">
							<small>
								<i className="fa fa-info-circle me-2"></i>
								<strong>No se puede finalizar:</strong> {getMotivoNoFinalizacion(estadosActuales, pedido.tipo)}
							</small>
						</Alert>
					)}
				</div>

				{/* Información adicional */}
				<div className="estados-info-adicional">
					<small className="text-muted">
						<i className="fa fa-info-circle me-1"></i>
						<strong>Nota:</strong> Los estados deben completarse en orden secuencial.
						El estado "Finalizado" cierra permanentemente el pedido y no permite más modificaciones.
					</small>
				</div>
			</Card.Body>
		</Card>
	)
}

export default EstadosTab